/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  IPresetPayload,
  IScenarioPayload,
  ISimulationPayload,
  IUnitPresetPayload,
} from '@schemas/scenarios-simulator/types';
import {
  ICMSTypeEnum,
  EnergyTypeEnum,
  ProductTypeEnum,
  CompanyTypeEnum,
  GuaranteeTypeEnum,
  ManagementTerminationFineEnum,
  ManagerTypeEnum,
  ModalityTypeEnum,
} from '@schemas/scenarios-simulator/enums';
import { TARIFF } from '@utils/constants';
import { fillConsumptionInfoMonths } from '@utils/scenarios-simulator/consumption-helpers';
import { getTariffModality } from '@utils/tariff-helpers';
import { mapProductType } from '@utils/scenarios-simulator/mappers/product-type-mapper';

const VALID_ICMS_TYPES = new Set(Object.values(ICMSTypeEnum));
const VALID_ENERGY_TYPES = new Set(Object.values(EnergyTypeEnum));
const VALID_PRODUCT_TYPES = new Set(Object.values(ProductTypeEnum));
const VALID_COMPANY_TYPES = new Set(Object.values(CompanyTypeEnum));
const VALID_TARIFF_GROUPS = new Set(Object.keys(TARIFF));
const VALID_PRESET_TYPES = new Set(['ACR', 'CLARKE_ACL', 'COMPETITOR_ACL']);

// Legacy numeric → current string enum migration maps
const LEGACY_ICMS_MAP: Record<number, ICMSTypeEnum> = {
  1: ICMSTypeEnum.ICMS_INDUSTRIAL,
  2: ICMSTypeEnum.ICMS_COMERCIAL,
  3: ICMSTypeEnum.ICMS_RURAL,
};
const LEGACY_ENERGY_MAP: Record<number, EnergyTypeEnum> = {
  0: EnergyTypeEnum.INCENTIVIZED_50,
  1: EnergyTypeEnum.INCENTIVIZED_100,
  2: EnergyTypeEnum.CONVENTIONAL,
};
const LEGACY_PRODUCT_MAP: Record<number, ProductTypeEnum> = {
  1: ProductTypeEnum.ACL_WHOLESALER,
  2: ProductTypeEnum.ACL_RETAILER,
  3: ProductTypeEnum.PRODUCT_2,
};
const LEGACY_TARIFF_MAP: Record<number, string> = {
  2: 'A4_GREEN',
  3: 'A4_BLUE',
  4: 'AS_BLUE',
  5: 'AS_GREEN',
  6: 'A3_BLUE',
  7: 'A2_BLUE',
  8: 'B3_STANDARD',
  9: 'B3_OPTING',
};
const LEGACY_GUARANTEE_MAP: Record<number, GuaranteeTypeEnum> = {
  1: GuaranteeTypeEnum.OTHER,
  2: GuaranteeTypeEnum.GUARANTEE_LETTER,
  3: GuaranteeTypeEnum.SURETY_BOND,
  6: GuaranteeTypeEnum.REGISTRATION_AGAINST_PAYMENT,
};
const LEGACY_TERMINATION_FINE_MAP: Record<number, ManagementTerminationFineEnum> = {
  3: ManagementTerminationFineEnum.THIRTY_PERCENT_OF_THE_CONTRACT_RESIDUAL,
  4: ManagementTerminationFineEnum.FIFTY_PERCENT_OF_THE_CONTRACT_RESIDUAL,
  5: ManagementTerminationFineEnum.HAS_A_FINE,
  6: ManagementTerminationFineEnum.NO_FINE,
};
const LEGACY_MANAGER_MAP: Record<number, ManagerTypeEnum> = {
  1: ManagerTypeEnum.INDEPENDENT_MANAGER,
  2: ManagerTypeEnum.SALES_MANAGER,
};
const LEGACY_MODALITY_MAP: Record<number, ModalityTypeEnum> = {
  0: ModalityTypeEnum.WHOLESALER,
  1: ModalityTypeEnum.RETAILER,
};

const VALID_GUARANTEE_TYPES = new Set(Object.values(GuaranteeTypeEnum));
const VALID_TERMINATION_FINES = new Set(Object.values(ManagementTerminationFineEnum));
const VALID_MANAGER_TYPES = new Set(Object.values(ManagerTypeEnum));
const VALID_MODALITY_TYPES = new Set(Object.values(ModalityTypeEnum));

function sanitizeEnum<T extends string>(
  value: unknown,
  validSet: Set<string>,
  legacyMap?: Record<number, T>,
): T | undefined {
  if (typeof value === 'string' && validSet.has(value)) return value as T;
  if (typeof value === 'number' && legacyMap && legacyMap[value] !== undefined) return legacyMap[value];
  return undefined;
}

function migrateLegacyDemandFields(preset: any): any {
  if (!preset) return preset;
  const ci = preset.consumptionInfo;
  if (!ci) return preset;

  const legacyDemandKeys = [
    'demandPeak',
    'demandOffPeak',
    'demandWithoutDiscountPeak',
    'demandWithoutDiscountOffPeak',
    'demandWithDiscountPeak',
    'demandWithDiscountOffPeak',
  ];

  const hasLegacyDemand = legacyDemandKeys.some((k) => ci[k] !== undefined && ci[k] !== null);
  if (!hasLegacyDemand) return preset;

  const contractedDemandInfo = {
    ...(ci.contractedDemandInfo ?? {}),
    ...(ci.demandPeak !== undefined ? { contractedDemandPeak: ci.demandPeak } : {}),
    ...(ci.demandOffPeak !== undefined ? { contractedDemandOffPeak: ci.demandOffPeak } : {}),
    ...(ci.demandWithoutDiscountPeak !== undefined
      ? { contractedDemandWithoutDiscountPeak: ci.demandWithoutDiscountPeak }
      : {}),
    ...(ci.demandWithoutDiscountOffPeak !== undefined
      ? { contractedDemandWithoutDiscountOffPeak: ci.demandWithoutDiscountOffPeak }
      : {}),
    ...(ci.demandWithDiscountPeak !== undefined ? { contractedDemandWithDiscountPeak: ci.demandWithDiscountPeak } : {}),
    ...(ci.demandWithDiscountOffPeak !== undefined
      ? { contractedDemandWithDiscountOffPeak: ci.demandWithDiscountOffPeak }
      : {}),
  };

  const restCi = Object.fromEntries(Object.entries(ci).filter(([k]) => !legacyDemandKeys.includes(k)));

  return {
    ...preset,
    consumptionInfo: { ...restCi, contractedDemandInfo },
  };
}

function sanitizePreset(preset: any): any {
  if (!preset) return preset;
  const migrated = migrateLegacyDemandFields(preset);
  return {
    ...migrated,
    icmsType: sanitizeEnum(preset.icmsType, VALID_ICMS_TYPES, LEGACY_ICMS_MAP),
    energyType: sanitizeEnum(preset.energyType, VALID_ENERGY_TYPES, LEGACY_ENERGY_MAP),
    productType: sanitizeEnum(preset.productType, VALID_PRODUCT_TYPES, LEGACY_PRODUCT_MAP),
    companyType: sanitizeEnum(preset.companyType, VALID_COMPANY_TYPES),
    tariffGroup: sanitizeEnum(preset.tariffGroup, VALID_TARIFF_GROUPS, LEGACY_TARIFF_MAP),
    guaranteeType: sanitizeEnum(preset.guaranteeType, VALID_GUARANTEE_TYPES, LEGACY_GUARANTEE_MAP),
    managementTerminationFine: sanitizeEnum(
      preset.managementTerminationFine,
      VALID_TERMINATION_FINES,
      LEGACY_TERMINATION_FINE_MAP,
    ),
    energyTerminationFine: sanitizeEnum(
      preset.energyTerminationFine,
      VALID_TERMINATION_FINES,
      LEGACY_TERMINATION_FINE_MAP,
    ),
    managerType: sanitizeEnum(preset.managerType, VALID_MANAGER_TYPES, LEGACY_MANAGER_MAP),
    modalityType: sanitizeEnum(preset.modalityType, VALID_MODALITY_TYPES, LEGACY_MODALITY_MAP),
    edc: typeof preset.edc === 'number' ? undefined : preset.edc,
    aux__useScenarioSelector: sanitizeEnum(preset.aux__useScenarioSelector, VALID_PRESET_TYPES),
    rawEnergyPrice: preset.rawEnergyPrice
      ? {
          ...preset.rawEnergyPrice,
          energyType: sanitizeEnum(preset.rawEnergyPrice.energyType, VALID_ENERGY_TYPES, LEGACY_ENERGY_MAP),
        }
      : preset.rawEnergyPrice,
  };
}

export function sanitizeLegacyPayload(payload: ISimulationPayload): ISimulationPayload {
  const parsedPayload = payload?.inputs?.parsedPayload;
  if (!parsedPayload) return payload;

  const sanitizeScenario = (scenario: any) =>
    scenario
      ? {
          ...scenario,
          scenarioPayload: scenario.scenarioPayload?.map((u: any) => ({
            ...u,
            presetPayload: sanitizePreset(u.presetPayload),
          })),
        }
      : scenario;

  return {
    ...payload,
    inputs: {
      ...payload.inputs,
      parsedPayload: {
        ...parsedPayload,
        baseScenario: sanitizeScenario(parsedPayload.baseScenario),
        targetScenarios: parsedPayload.targetScenarios?.map(sanitizeScenario),
      },
    },
  };
}

export const removeAuxUseFields = <T>(value: T, path = ''): T => {
  if (Array.isArray(value)) {
    return value.map((item, idx) => removeAuxUseFields(item, `${path}[${idx}]`)) as T;
  }

  if (value !== null && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, val]) => {
      if (key.startsWith('aux__use')) {
        return acc;
      }

      acc[key] = removeAuxUseFields(val, `${path}.${key}`);
      return acc;
    }, {} as any);
  }

  return value;
};

export const removeMonthYearPrefix = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map(removeMonthYearPrefix) as T;
  }

  if (value !== null && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, val]) => {
      let newKey = key;

      if (key.startsWith('month')) {
        newKey = key.replace(/^month/, '');
      }

      if (key.startsWith('year')) {
        newKey = key.replace(/^year/, '');
      }

      acc[newKey] = removeMonthYearPrefix(val);
      return acc;
    }, {} as any);
  }

  return value;
};

const normalizeMarginFee = (marginFee: any): any => {
  if (!marginFee) return marginFee;

  const normalized = {
    ...marginFee,
    type: marginFee.type ?? 'ABSOLUTE',
    values: marginFee.values ?? {},
    aux__useSamePricePerYears: marginFee.aux__useSamePricePerYears ?? false,
  };

  if (normalized.type === 'PERCENTAGE' && normalized.values) {
    return {
      ...normalized,
      values: Object.entries(normalized.values).reduce((acc, [key, value]) => {
        acc[key] = typeof value === 'number' ? value / 100 : value;
        return acc;
      }, {} as Record<string, any>),
    };
  }

  return normalized;
};
export const normalizePresetPayload = (
  preset: IPresetPayload,
  nickname: string,
  presetType: IUnitPresetPayload['presetType'],
): any => {
  let consumptionInfo = preset.consumptionInfo;
  if (consumptionInfo) {
    consumptionInfo = fillConsumptionInfoMonths(consumptionInfo);

    if (consumptionInfo.contractedDemandInfo) {
      const cd = { ...consumptionInfo.contractedDemandInfo };
      const isSplitDemandFields =
        presetType !== 'ACR' &&
        (preset.isAlreadyAtAclBeforeNewRule === true || String(preset.isAlreadyAtAclBeforeNewRule) === 'true') &&
        preset.energyType !== 'CONVENTIONAL';

      const isBlue = getTariffModality(String(preset.tariffGroup)) === 'BLUE';

      if (isSplitDemandFields) {
        cd.contractedDemandPeak = undefined;
        cd.contractedDemandOffPeak = undefined;

        if (!isBlue) {
          cd.contractedDemandWithoutDiscountPeak = undefined;
        } else if (!cd.contractedDemandWithoutDiscountPeak && cd.contractedDemandWithoutDiscountOffPeak) {
          cd.contractedDemandWithoutDiscountPeak = cd.contractedDemandWithoutDiscountOffPeak;
        }
      } else {
        cd.contractedDemandWithoutDiscountPeak = undefined;
        cd.contractedDemandWithoutDiscountOffPeak = undefined;
        cd.contractedDemandWithDiscountPeak = undefined;
        cd.contractedDemandWithDiscountOffPeak = undefined;
        if (!isBlue) {
          cd.contractedDemandPeak = undefined;
        }
      }

      consumptionInfo = { ...consumptionInfo, contractedDemandInfo: cd };
    }
  }
  return {
    ...preset,
    nickname,
    consumptionInfo,
    powerGeneratorCost:
      typeof preset.powerGeneratorCost === 'number' && !Number.isNaN(preset.powerGeneratorCost)
        ? preset.powerGeneratorCost
        : 0,
    productType:
      preset.productType ?? (preset.aux__useProductType ? mapProductType(preset.aux__useProductType) : undefined),
    energyType: preset.energyType,
    guaranteeType: preset.guaranteeType,
    rawEnergyPrice: preset.rawEnergyPrice
      ? {
          ...preset.rawEnergyPrice,
          energyType:
            typeof preset.rawEnergyPrice.energyType === 'number'
              ? ({ 0: 'INCENTIVIZED_50', 1: 'INCENTIVIZED_100', 2: 'CONVENTIONAL' } as Record<number, string>)[
                  preset.rawEnergyPrice.energyType as number
                ] ?? 'INCENTIVIZED_50'
              : preset.rawEnergyPrice.energyType,
        }
      : preset.rawEnergyPrice,
    energyPriceInfo: preset.energyPriceInfo,
    marginFee: normalizeMarginFee(preset.marginFee),
    useIRecCertification:
      typeof preset.useIRecCertification === 'string'
        ? preset.useIRecCertification === 'true'
        : Boolean(preset.useIRecCertification),
  };
};

export const handleScenarioPayload = (scenarioPayload: IUnitPresetPayload, isAlreadyAtAcl?: boolean) => {
  let presetPayload = normalizePresetPayload(
    scenarioPayload?.presetPayload as IPresetPayload,
    scenarioPayload?.unitPayload?.nickname as string,
    scenarioPayload?.presetType,
  );
  if (scenarioPayload?.presetType === 'COMPETITOR_ACL' && scenarioPayload?.unitPayload?.id) {
    presetPayload = {
      ...presetPayload,
      competitorId: scenarioPayload.unitPayload.id,
    };
  }
  return {
    ...scenarioPayload,
    presetPayload,
  };
};

const parseScenario = (payload: ISimulationPayload) => {
  const baseScenario: IScenarioPayload = payload?.inputs?.parsedPayload?.baseScenario as IScenarioPayload;
  const targetScenarios: IScenarioPayload[] = payload?.inputs?.parsedPayload?.targetScenarios as IScenarioPayload[];

  return {
    ...payload?.inputs?.parsedPayload,
    baseScenario: {
      scenarioIndex: 0,
      scenarioName: baseScenario?.scenarioName ?? 'Cenário Atual',
      scenarioPayload: baseScenario?.scenarioPayload,
    },
    targetScenarios: targetScenarios?.map((targetScenario, idx) => ({
      scenarioIndex: idx + 1,
      scenarioName: targetScenario?.scenarioName,
      scenarioPayload: targetScenario?.scenarioPayload,
    })),
  };
};
export function parseSimulationPayload(payload: ISimulationPayload): ISimulationPayload {
  return {
    ...payload,
    inputs: {
      ...payload.inputs,
      parsedPayload: parseScenario(payload),
    },
  } as ISimulationPayload;
}
